import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminIcon, CopyClipboardIcon } from '@tanium/react-graphics';
import { TooltippedButton } from '@tanium/react-button';
import { DefinitionList, DefinitionListItem } from '@tanium/react-definition-list';
import { PageSection } from '@tanium/react-page-section';
import { Well } from '@tanium/react-well';
import { TextWithIcon } from '@tanium/react-text-with-icon';
import { Stack, StackChild } from '@tanium/react-stack';
import { ThemeColors } from '@tanium/theme-data';
import { useTenant } from '../../hooks/useTenant';
import { useHasPermissions } from '../../hooks/useUserPermissions';
import { Permission } from '../../models/Permissions';
import { UpdateWorkspaceModal } from './UpdateWorkspaceModal';
import { UpdateAdminModal } from './UpdateAdminModal';

export const AccountDetailsSection: React.VFC = () => {
  const { t } = useTranslation();
  const { tenant, updateWorkspace } = useTenant();
  const canUpdateRootAdminUser = useHasPermissions([Permission.UpdateAdminSettings]);
  const [updateAdminModalOpen, setUpdateAdminModalOpen] = useState(false);
  const [updateWorkspaceModalOpen, setWorkspaceModalOpen] = useState(false);

  const directLoginUrl = tenant?.workspace
    ? `${window.location.origin}/workspace/${tenant.workspace}`
    : undefined;

  return (
    <>
      <PageSection title={<TextWithIcon leadingIcon={AdminIcon}>{t('Account Details')}</TextWithIcon>}>
        <Well bg={ThemeColors.Light0} marginBottom="8px">
          <Stack alignItems="center" justifyContent="space-between" width="100%">
            <DefinitionList>
              <DefinitionListItem term={`${t('Workspace')}:`} description={tenant?.workspace} />
            </DefinitionList>
            <StackChild>
              <Stack itemSpacing={1}>
                {directLoginUrl && (
                  <TooltippedButton
                    variant="secondary"
                    tooltipContent={directLoginUrl}
                    onClick={() => {
                      void navigator.clipboard?.writeText(directLoginUrl);
                    }}
                  >
                    <TextWithIcon leadingIcon={CopyClipboardIcon}>{t('Direct Login Link')}</TextWithIcon>
                  </TooltippedButton>
                )}
                <TooltippedButton
                  variant="secondary"
                  disabled={!canUpdateRootAdminUser}
                  tooltipContent={!canUpdateRootAdminUser && t('Insufficient permissions to change the workspace')}
                  onClick={() => {
                    setWorkspaceModalOpen(true);
                  }}
                >
                  {t('Change Workspace')}
                </TooltippedButton>
              </Stack>
            </StackChild>
          </Stack>
        </Well>
        <Well bg={ThemeColors.Light0}>
          <Stack alignItems="center" itemSpacing={1}>
            <DefinitionList>
              <DefinitionListItem term={`${t('Username')}:`} description={tenant?.username} />
            </DefinitionList>
            <StackChild>|</StackChild>
            <StackChild grow={1}>
              <DefinitionList>
                <DefinitionListItem term={`${t('Email Address')}:`} description={tenant?.email} />
              </DefinitionList>
            </StackChild>
            <StackChild>
              <Stack itemSpacing={1}>
                <TooltippedButton
                  variant="secondary"
                  disabled={!canUpdateRootAdminUser}
                  tooltipContent={
                    !canUpdateRootAdminUser && t('Insufficient permissions to change the root admin user')
                  }
                  onClick={() => {
                    setUpdateAdminModalOpen(true);
                  }}
                >
                  {t('Change Admin')}
                </TooltippedButton>
              </Stack>
            </StackChild>
          </Stack>
        </Well>
      </PageSection>
      <UpdateWorkspaceModal
        tenantId={tenant?.id}
        isOpen={updateWorkspaceModalOpen}
        onRequestClose={() => setWorkspaceModalOpen(false)}
        updateWorkspace={updateWorkspace}
      />
      <UpdateAdminModal
        tenantId={tenant?.id}
        currentAdminUser={tenant?.username}
        isOpen={updateAdminModalOpen}
        onRequestClose={() => setUpdateAdminModalOpen(false)}
      />
    </>
  );
};
